import getHostUserJwtToken from "@/app/[tenantId]/[messengerId]/[hostConfig]/[locale]/lib/client/utils/getHostUserJwtToken";
import type { LauncherContextType } from "@/app/[tenantId]/[messengerId]/[hostConfig]/lib/client/contexts/LauncherContext";
import type {
  CreateConversationRequest,
  CreateConversationResponse,
  CreateDeviceChannelRequest,
  CreateDeviceChannelResponse,
  CreateNotificationRequest,
  CreateNotificationResponse,
  CreateUploadSessionRequest,
  CreateUploadSessionResponse,
  RefreshMessengerTokenRequest,
  RefreshMessengerTokenResponse,
} from "@deskpro-product/messenger-api-schemas/messenger/api/conversation/mutation";
import type {
  ConversationResponse,
  ConversationsResponse,
} from "@deskpro-product/messenger-api-schemas/messenger/api/conversation/query";
import type { BusinessHoursResponse } from "@deskpro-product/messenger-api-schemas/messenger/api/business_hours/query";
import type {
  HelpcenterContentRequest,
  HelpcenterContentResponse,
  HelpcenterSearchResponse,
} from "@deskpro-product/messenger-api-schemas/messenger/api/content/query";
import type {
  ArticlesCategoryContentRequest,
  ArticlesCategoryContentResponse,
} from "@deskpro-product/messenger-api-schemas/messenger/api/articles/query";
import type {
  LatestNewsRequest,
  NewsPostsRequest,
  NewsPostsResponse,
} from "@deskpro-product/messenger-api-schemas/messenger/api/news/query";
import type { MutableRefObject } from "react";

function jsonResponseCallback(res: Response) {
  return res.json();
}

function getParams(requestParams?: Record<string, string | string[]>) {
  if (!requestParams || Object.keys(requestParams).length === 0) {
    return "";
  }
  const p = new URLSearchParams();
  for (const key in requestParams) {
    const value = requestParams[key];
    if (Array.isArray(value)) {
      for (const v of value) {
        p.append(key, v);
      }
    } else {
      p.append(key, value);
    }
  }
  return `?${p.toString()}`;
}

async function request<Req, Res>(
  method: "POST" | "GET",
  url: string,
  authorization: string,
  extraHeaders: Record<string, string>,
  body: Req | undefined,
  responseCallback: (response: Response) => Promise<Res>,
  errorCallback: (response: Response) => Promise<Res>,
): Promise<Res> {
  const headers: HeadersInit = {
    ...extraHeaders,
    authorization,
  };

  const requestInit: RequestInit = {
    method,
    headers,
  };

  if (body) {
    headers["Content-Type"] = "application/json";
    requestInit.body = JSON.stringify(body);
  }

  const response = await fetch(url, requestInit);
  if (response.ok) {
    return responseCallback(response);
  }
  return await errorCallback(response);
}

function error<Res>(endpoint: string, response: Response): Promise<Res> {
  throw new Error(`Error calling ${endpoint} got (${response.status}) ${response.statusText}`);
}

export class MessengerApiClient {
  private tenantId: string;
  private messengerId: string;
  private messengerToken: string;
  private connection: LauncherContextType["connection"];
  private authorization: MutableRefObject<string | null>;
  private setAuth: (auth: string) => void;
  private reset: () => void;

  constructor(
    tenantId: string,
    messengerId: string,
    messengerToken: string,
    connection: LauncherContextType["connection"],
    authorization: MutableRefObject<string | null>,
    setAuth: (auth: string) => void,
    reset: () => void,
  ) {
    this.tenantId = tenantId;
    this.messengerId = messengerId;
    this.messengerToken = messengerToken;
    this.connection = connection;
    this.authorization = authorization;
    this.setAuth = setAuth;
    this.reset = reset;

    // Bind the functions on this class to itself so that they can be used detached; for example, in useApi()
    Object.entries(Object.getOwnPropertyDescriptors(Reflect.getPrototypeOf(this)))
      .filter(([n, d]) => typeof d.value === "function" && n !== "constructor")
      .forEach(([n, d]) => {
        this[n as keyof this] = d.value.bind(this);
      });
  }

  async client<Req, Res>(
    args: {
      endpoint: string;
      extraHeaders: Record<string, string>;
      responseCallback: (res: Response) => Promise<Res>;
    } & (
      | {
          method: "POST";
          requestBody?: Req;
        }
      | {
          method: "GET";
          requestParams?: Record<string, string | string[]>;
        }
    ),
  ): Promise<Res> {
    const { method, endpoint, extraHeaders, responseCallback } = args;

    let params = "";
    let body = undefined;

    switch (method) {
      case "GET":
        params = getParams(args.requestParams);
        break;
      case "POST":
        body = args.requestBody;
        break;
      default:
        method satisfies never;
    }

    const origin = import.meta.env.VITE_MESSENGER_API_ORIGIN ?? window.location.origin;
    const path = import.meta.env.VITE_MESSENGER_API_PATH ?? "";
    const url = `${origin}${path}/${this.tenantId}/messenger/${this.messengerId}/${endpoint}${params}`;

    const auth = this.authorization.current;
    if (!auth) {
      throw new Error("No auth token");
    }
    return await request(
      method,
      url,
      auth,
      extraHeaders,
      body,
      responseCallback,
      async (response) => {
        if (response.status === 401) {
          const newAuth = await this.refreshAuthToken();
          return await request(method, url, newAuth, extraHeaders, body, responseCallback, (res) =>
            error(endpoint, res),
          );
        }
        return error(endpoint, response);
      },
    );
  }

  private async clientMutation<Req, Res>(
    endpoint: string,
    {
      requestBody,
      extraHeaders,
    }: {
      requestBody?: Req;
      extraHeaders?: Record<string, string>;
    },
  ): Promise<Res> {
    try {
      return this.client<Req, Res>({
        method: "POST",
        endpoint,
        requestBody,
        extraHeaders: {
          ...extraHeaders,
          "X-Deskpro-Token": this.messengerToken || "",
          Accept: "application/json",
        },
        responseCallback: jsonResponseCallback,
      });
    } catch (e) {
      console.error("Mutation error", e);
      throw e;
    }
  }

  private async clientQuery<Res>(
    endpoint: string,
    params?: {
      requestParams?: Record<string, string | string[]>;
      extraHeaders?: Record<string, string>;
      responseCallback?: (res: Response) => Promise<Res>;
    },
  ): Promise<Res> {
    const { requestParams, extraHeaders } = params || {};
    const responseCallback = params?.responseCallback || jsonResponseCallback;
    return this.client<void, Res>({
      method: "GET",
      endpoint,
      requestParams,
      extraHeaders: !params?.responseCallback
        ? {
            ...extraHeaders,
            "X-Deskpro-Token": this.messengerToken || "",
            Accept: "application/json",
          }
        : extraHeaders || {},
      responseCallback,
    });
  }

  async refreshAuthToken() {
    const connection = this.connection.current;
    if (!connection) {
      throw new Error("Cannot refresh auth token because there is no connection");
    }
    const auth = this.authorization.current;
    if (!auth) {
      throw new Error("Cannot refresh auth token because there is no auth token");
    }
    const hostUserJwtToken = await getHostUserJwtToken(this.messengerId, connection);
    const newToken = await this.getMessengerTokenClient({
      messengerToken: auth,
      token: hostUserJwtToken,
    });
    this.setAuth(newToken);
    return newToken;
  }

  private async getMessengerTokenClient(
    requestBody: RefreshMessengerTokenRequest,
    retry = 0,
  ): Promise<string> {
    for (let attempt = 0; attempt <= retry; attempt++) {
      try {
        return await this.clientMutation<
          RefreshMessengerTokenRequest,
          RefreshMessengerTokenResponse
        >("user/auth/refresh-token", {
          requestBody,
        }).then((r) => r.messengerToken);
      } catch (e) {
        await new Promise<void>((resolve) => setTimeout(resolve, (attempt + 1) * 1000));
      }
    }
    this.reset();
    throw new Error("Unable to refresh access token");
  }

  async createConversation(
    requestBody: CreateConversationRequest,
  ): Promise<CreateConversationResponse> {
    return this.clientMutation<CreateConversationRequest, CreateConversationResponse>(
      "conversation",
      {
        requestBody,
      },
    );
  }

  async getConversations(): Promise<ConversationsResponse> {
    return this.clientQuery<ConversationsResponse>("conversation");
  }

  async getConversation(conversationId: string, cursor?: string): Promise<ConversationResponse> {
    return this.clientQuery<ConversationResponse>(`conversation/${conversationId}`, {
      requestParams: {
        cursor: cursor || "",
      },
    });
  }

  async getHelpCenterContents(
    params: HelpcenterContentRequest["Querystring"],
  ): Promise<HelpcenterContentResponse> {
    return this.clientQuery<HelpcenterContentResponse>("help-center/content", {
      requestParams: {
        ...params,
      },
    });
  }

  async getBlob(conversation: string, blob: string) {
    return this.clientQuery<Blob>(`conversation/${conversation}/attachment/${blob}`, {
      responseCallback: (res) => res.blob(),
    });
  }

  async getArticlesCategoryContent(
    categoryId: string,
    params: ArticlesCategoryContentRequest["Querystring"],
  ): Promise<ArticlesCategoryContentResponse> {
    return this.clientQuery<ArticlesCategoryContentResponse>(
      `articles/category/${categoryId}/content`,
      {
        requestParams: {
          withSubcategories: params.withSubcategories ? "true" : "false",
          sortOrder: "asc" as const,
          sortBy: "title" as const,
        },
      },
    );
  }

  async getHelpCenterSearch(query: string): Promise<HelpcenterSearchResponse> {
    return this.clientQuery<HelpcenterSearchResponse>("help-center/search", {
      requestParams: {
        query,
      },
    });
  }

  async getLatestNewsPosts(params: LatestNewsRequest["Querystring"]): Promise<NewsPostsResponse> {
    return this.clientQuery<NewsPostsResponse>("news/latest", {
      requestParams: {
        limit: params.limit?.toString() ?? "",
        categoryIds: params.categoryIds ?? "",
      },
    });
  }

  async getNewsPosts(params: NewsPostsRequest["Querystring"]): Promise<NewsPostsResponse> {
    return this.clientQuery<NewsPostsResponse>("news", {
      requestParams: {
        postIds: params.postIds ?? "",
        sortBy: params.sortBy ?? "latest",
        sortOrder: params.sortOrder ?? "desc",
        limit: params.limit?.toString() ?? "5",
      },
    });
  }

  async createDeviceChannel(
    deviceId: string,
    requestBody: CreateDeviceChannelRequest,
  ): Promise<CreateDeviceChannelResponse> {
    const result = await this.clientMutation<
      CreateDeviceChannelRequest,
      CreateDeviceChannelResponse
    >(`device/${deviceId}/channel`, {
      requestBody,
    });

    return result;
  }

  async createUploadSession(deviceId: string, conversationId: string) {
    return await this.clientMutation<CreateUploadSessionRequest, CreateUploadSessionResponse>(
      `device/${deviceId}/${conversationId}/uploadSession`,
      {
        requestBody: {},
      },
    );
  }

  async sendNotificationMutation(
    deviceId: string,
    notification: CreateNotificationRequest,
  ): Promise<CreateNotificationResponse> {
    const result = await this.clientMutation<CreateNotificationRequest, CreateNotificationResponse>(
      `device/${deviceId}/notification`,
      {
        requestBody: notification,
      },
    );

    return result;
  }

  async getBusinessHours(setIds: string[]) {
    return await this.clientQuery<BusinessHoursResponse>("business-hours", {
      requestParams: {
        period: "P7D",
        setIds: setIds.join(","),
      },
    });
  }
}
